import { useState, type ReactNode } from "react";
import {
  apiFetch,
  Button,
  ContactUsModal,
  type ContactMessagePrefill,
  type ContactTopic,
  type PortalMe,
  type RequestCallPrefill,
} from "@dearbill/shared";

/** Member support topics, in plain English because the member SPA has no i18n. */
const MEMBER_TOPICS: ContactTopic[] = [
  { value: "My enrollment status", label: "My enrollment status" },
  { value: "Billing & payments", label: "Billing & payments" },
  { value: "Update my information", label: "Update my information" },
  { value: "Technical problem", label: "Technical problem" },
  { value: "Something else", label: "Something else" },
];

/** Per-tab prefill resolved from `/portal/me`. */
interface MemberPrefill {
  call: RequestCallPrefill;
  message: ContactMessagePrefill;
}

/**
 * "Contact us" trigger for the member portal header. It opens the shared
 * two-tab `ContactUsModal` with its English defaults, because the member SPA has
 * no i18n. With a portal session it resolves `GET /api/portal/me` before opening
 * and prefills both tabs. The call tab gets the name and phone, all editable,
 * because the member may want the call on a different number. The message tab
 * gets the email and phone, with the phone read-only and the email read-only
 * when one is on file. The session token rides the message POST, which is
 * harmless because `/contact` is public. Without a session, as on the login
 * screen, the forms open blank.
 */
export function MemberContactButton({
  session,
}: {
  session?: string | null | undefined;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState<MemberPrefill | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const baseUrl = import.meta.env.VITE_API_BASE_URL ?? "";

  const openModal = (): void => {
    if (!session) {
      setPrefill(undefined);
      setOpen(true);
      return;
    }
    setLoading(true);
    apiFetch<PortalMe>("/api/portal/me", {}, { baseUrl, token: session })
      .then((me) => {
        setPrefill({
          call: { firstName: me.firstName, lastName: me.lastName, phone: me.phone },
          message: {
            phone: me.phone,
            phoneReadOnly: true,
            email: me.email ?? "",
            emailReadOnly: me.email !== null,
          },
        });
      })
      // Degrade to blank forms if /me is unavailable rather than blocking.
      .catch(() => setPrefill(undefined))
      .finally(() => {
        setLoading(false);
        setOpen(true);
      });
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        disabled={loading}
        onClick={openModal}
        className="border-haze text-midnight hover:bg-ice border-[1.5px] bg-transparent px-[21px] py-2.5 text-[15px] font-semibold hover:opacity-100"
      >
        Contact us
      </Button>
      <ContactUsModal
        open={open}
        onClose={() => setOpen(false)}
        source="member"
        baseUrl={baseUrl}
        topics={MEMBER_TOPICS}
        {...(prefill ? { callPrefill: prefill.call, messagePrefill: prefill.message } : {})}
        {...(session ? { token: session } : {})}
      />
    </>
  );
}
